import { type ApiRequisition } from '@Model';
import {
    defineWizardStep,
    isLinked,
    type AsyncStateUpdate,
    type WizardStep,
    type WizardStepErrors,
} from '@synqo/frontend-component-library';
import { type RequisitionWizardState } from '../../RequisitionWizardState';
import { materialErrorKey, quantityErrorKey, type PositionsStepErrorKey } from './positionErrorKeys';
import { PositionsStepBody } from './PositionsStepBody';

interface DefinePositionsStepProps {
    data: ApiRequisition;
    apiResourcePath: string;
    canEdit: boolean;
    /** Overrides the stepper label, e.g. with the action verb of a container-aware wizard. */
    label?: string;
    /** Passed through to the body: the counter beside the title, off inside the container flow. */
    withCounter?: boolean;

    requestUpdateState: AsyncStateUpdate<ApiRequisition>;
    i18n: (key: string) => string;
}

// @ai-generated 2026-07-03T19:30:00Z by dimitri.driediger@synqony.com using Claude Fable 5
export function definePositionsStep({
    data,
    apiResourcePath,
    canEdit,
    label,
    withCounter,
    requestUpdateState,
    i18n,
}: DefinePositionsStepProps): WizardStep<RequisitionWizardState> {
    return defineWizardStep<RequisitionWizardState, PositionsStepErrorKey>({
        key: 'positions',
        label: label ?? i18n('Positionen'),
        render: () => (
            <PositionsStepBody
                data={data}
                apiResourcePath={apiResourcePath}
                canEdit={canEdit}
                withCounter={withCounter}
                requestUpdateState={requestUpdateState}
            />
        ),
        validate: () => {
            const errors: WizardStepErrors<PositionsStepErrorKey> = {};
            const positions = data.positions;
            if (positions.length === 0) {
                errors.positions = i18n('Mindestens eine Position ist erforderlich.');
                return errors;
            }
            for (const position of positions) {
                if (!isLinked(position.service)) {
                    errors[materialErrorKey(position.id)] = i18n('Bitte wähle ein Material aus.');
                }
                if (!(position.quantity > 0)) {
                    errors[quantityErrorKey(position.id)] = i18n('Bitte gib eine Menge größer 0 an.');
                }
            }
            return errors;
        },
    });
}
